import React, {SetStateAction, useEffect, useState} from 'react';
import {Button, Modal} from 'react-bootstrap';
import {CustomAutocomplete} from '../../../../shared/components/Autocomplete';
import {useTranslation} from 'react-i18next';
import {calculateUploadUntil, formatDate, removeTimeZoneFromDate} from '../../../../shared/functions/Functions';
import {iManageList} from '../../../brand/campaign/manage/calendar/dto/IManageCalendar';
import {IOfferUpdate} from '../../../brand/campaign/book/offer/dto/IUpdateOffer';
import {CustomImageHandler} from '../../../../shared/components/CustomImageHandler';
import {InfluencerPlannerService} from '../service';
import {ErrorToast, SuccessToast} from '../../../../utils/toasters';
import {useDispatch, useSelector} from 'react-redux';
import {IAllStates} from '../../../../store/rootReducer';
import {plannerInfluencer} from '../../../../store/influencer/plannerSlice';
import CustomDatePicker from '../../../../shared/components/DatePicker';

interface IProposalModalProps {
    state: iManageList;
    setState: React.Dispatch<SetStateAction<iManageList>>;
    isModalOpened: boolean;
    handleCloseModal: () => void;

}

const ProposalModal = ({
                           state,
                           setState,
                           isModalOpened,
                           handleCloseModal,
                       }: IProposalModalProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {events} = useSelector((reduxState: IAllStates) => reduxState.plannerInfluencer);
    const [selectedOffer, setSelectedOffer] = useState<IOfferUpdate>();
    const selectedPlatform = state?.offerItem;
    const isProposalEdited = formatDate(state?.postingDate) !== formatDate(state?.date);
    const handleChangeTimepicker = (newValue: Date | null, name: string) => {
        setState(prev => ({...prev, [name]: newValue}));
    };

    const numReviewsDays = selectedOffer?.offer?.campaign?.isReviewed ? selectedOffer?.offer?.campaign?.reviewDays : 0;
    const changeOfferHandler = (paramKey: string, paramValue: string | number) => {
        setState(prev => ({...prev, [paramKey]: +paramValue}));
    };
    const handleCreateEvent = async () => {
        await InfluencerPlannerService.acceptEvent({
            eventId: state?.id,
            postingDate: state?.postingDate ?? state?.date,
        }, state?.id).then(response => {
            const copy = [...events];
            const updatedEventIndex = copy?.findIndex(event => event.id === state?.id);
            copy[updatedEventIndex] = response[0];
            dispatch(plannerInfluencer.actions.setEvents(response?.length === 1 ? copy : copy.concat(response[1])));
            SuccessToast(t('influencer.planner.successScheduled'));
            handleCloseModal();
        }).catch(error => ErrorToast(error));
    };
    useEffect(() => {
        if (state?.postingDate) {
            setSelectedOffer(state?.offerItem as unknown as IOfferUpdate);
        }
    }, [state]);

    return (
        <Modal
            show={isModalOpened}
            centered
            onHide={handleCloseModal}
            keyboard={false}
        >
            {state?.id && <Modal.Body>
                <div className="border-bottom mb-3">
                    <h5>
                        {t('influencer.planner.newPosting')}
                    </h5>
                </div>
                <div className="my-4 d-flex align-items-center">
                    <CustomImageHandler photoPath={selectedOffer?.offer?.user?.profilePhoto?.path as string}
                                        classes="small-rounded-logo"
                                        altTag="Social Platform icon"/>
                    <div className="ml-2">
                        <span className="font-weight-semi-bold">
                            {selectedOffer?.offer?.campaign?.title ?? '-'}
                        </span>
                        <div className={'d-flex align-items-center'}>
                            <div className="span text-primary">
                                {selectedOffer?.offer?.user?.displayName ?? '-'}
                            </div>
                            {selectedOffer?.offer?.isLongTerm && <div className={'custom-badge long-term ml-1'}>
                                {t(`brand.campaign.briefing.overview_card.linked`)}
                            </div>}
                            {selectedOffer?.offer?.isAddonsOnly && <div className={'custom-badge additional ml-1'}>
                                {t('influencer.offer_management.modal.additional')}
                            </div>}
                        </div>
                    </div>
                </div>
                <div className="my-4">
                    <CustomAutocomplete
                        dataArray={[]}
                        defaultArray={state?.offerItem as any}
                        isWithImage={true} inputLabel="socialPlatform.name" isMultiple={false}
                        photoLabel="socialPlatform.path"
                        selectedStateImage={selectedPlatform?.socialPlatform?.path as string}
                        queryFilterKey="offerItemId"
                        inputLabelTranslation={t('brand.campaign.manage.postingType')}
                        handleChangeParams={changeOfferHandler}/>
                </div>
                <div className="my-3">
                    <CustomDatePicker label={t('brand.campaign.manage.postingDate')}
                                      value={state?.postingDate}
                                      disableFrom={removeTimeZoneFromDate(state?.offerItem.offer?.campaign?.publishFrom ?? '')}
                                      disableTo={removeTimeZoneFromDate(state?.offerItem.offer?.campaign?.publishTo ?? '')}
                                      onChange={(event: Date | null) => handleChangeTimepicker(event, 'postingDate')}/>
                </div>
                <p>{t('influencer.planner.uploadContentUntil')}: {calculateUploadUntil(numReviewsDays, state?.postingDate as string)}</p>
            </Modal.Body>}
            <Modal.Footer>
                <Button variant="outline-primary" onClick={handleCloseModal}>
                    {t('general.button.cancel')}
                </Button>
                <Button variant="primary" onClick={handleCreateEvent}>
                    {t(isProposalEdited ? 'general.button.save' : 'general.button.accept')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ProposalModal;
